/*
 * Copyright (c) Akveo 2019. All Rights Reserved.
 * Licensed under the Single Application / Multi Application License.
 * See LICENSE_SINGLE_APP / LICENSE_MULTI_APP in the 'docs' folder for license information on type of purchased license.
 */
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router, ActivatedRoute  } from '@angular/router';
import { NB_AUTH_OPTIONS, NbAuthService, NbAuthResult } from '@nebular/auth';
import { getDeepFromObject } from '../../helpers';
import { environment } from '../../../../environments/environment';
import { ProfileService } from '../../../insurgo-services/profile.service';
import { Observable, of, timer } from 'rxjs';
import { debounceTime, switchMap, map, catchError } from 'rxjs/operators';
import { MustMatch } from '../../../insurgo-pages/profile/validator';

@Component({
  selector: 'ngx-verify',
  styleUrls: ['./verify.component.scss'],
  templateUrl: './verify.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NgxVerifyComponent implements OnInit {

  // minLength: number = this.getConfigValue('forms.validation.password.minLength');
  // maxLength: number = this.getConfigValue('forms.validation.password.maxLength');
  redirectDelay: number = this.getConfigValue('forms.resetPassword.redirectDelay');
  showMessages: any = this.getConfigValue('forms.resetPassword.showMessages');
  strategy: string = this.getConfigValue('forms.resetPassword.strategy');
  isPasswordRequired: boolean = this.getConfigValue('forms.validation.password.required');
  altText = environment.logoAltText;
  
  submitted = false;
  errors: string[] = [];
  messages: string[] = [];
  user: any = {};
  token: string = '';

  verifyForm: UntypedFormGroup;
  public passwordRequirements: string;
  constructor(protected service: NbAuthService,
    @Inject(NB_AUTH_OPTIONS) protected options = {},
    protected cd: ChangeDetectorRef,
    private fb: UntypedFormBuilder,
    protected router: Router,
    private route: ActivatedRoute,
    private _profileService: ProfileService) { }

  get password() { return this.verifyForm.get('password'); }
  get confirmPassword() { return this.verifyForm.get('confirmPassword'); }

  ngOnInit(): void {
    this.route.queryParams.subscribe(values => {
      if("token" in values) this.token = values.token;
    });

    this.verifyForm = this.fb.group({
      password: this.fb.control('', [Validators.required], [this.passwordValidator.bind(this)]),
      confirmPassword: this.fb.control('', [Validators.required]),
      token: this.token,
      verify: true
    }, {
      validator: MustMatch('password', 'confirmPassword')
    });

    this._profileService.getPasswordRequirements().subscribe(
      (data) => {
        this.passwordRequirements = data.message;
        this.cd.markForCheck();
      },
      (error) => {}
    );
  }
  // convenience getter for easy access to form fields
  get f() {
    return this.verifyForm.controls;
  }

  // Custom async validator for password validation
  passwordValidator(control: AbstractControl): Observable<ValidationErrors | null> {
    if (!control.value) {
      return of(null); // Let other validators handle required/minLength
    }

    // Use timer to create a debounced effect
    return timer(400).pipe(
      switchMap(() => 
        this._profileService.validatePassword(control.value).pipe(
          map(response => {
            if (!response.isValid) {
              return { passwordInvalid: response.errors };
            }
            return null;
          }),
          catchError(() => of({ passwordValidationError: true }))
        )
      )
    );
  }

  // Get formatted password error message
  getPasswordErrorMessage(): string {
    const errors = this.f.password.errors?.passwordInvalid;
    if (errors && Array.isArray(errors)) {
      return errors.join(', ');
    } else if (errors) {
      return errors.toString();
    }
    return 'Password does not meet requirements';
  }

  displayFieldCss(form: UntypedFormGroup, field: any) {
    if (form.dirty) {
      if (field.pending) {
        // Async validation in progress
        return "warning";
      } else if (field.dirty && field.invalid) {
        //invalid - show immediately when user starts typing
        return "danger";
      } else if (field.touched && field.valid) {
        //valid - only show after user leaves field
        return "success";
      } else {
        //needs to be filled in
        return "warning";
      }
    } else {
      return "basic";
    }
  }

  verify(): void {
    this.errors = this.messages = [];
    this.submitted = true;
    this.user = this.verifyForm.value;

    this.service.resetPassword(this.strategy, this.user).subscribe((result: NbAuthResult) => {
      this.submitted = false;
      if (result.isSuccess()) {
        this.messages = result.getMessages();
      } else {
        this.errors = result.getErrors();
      }

      const redirect = result.getRedirect();
      if (redirect) {
        setTimeout(() => {
          return this.router.navigateByUrl(redirect);
        }, this.redirectDelay);
      }
      this.cd.detectChanges();
    });
  }

  getConfigValue(key: string): any {
    return getDeepFromObject(this.options, key, null);
  }
}
